import { CircularProgress } from "@mui/material";
import { Form, Formik, FormikHelpers } from "formik";
import { useEffect, useState } from "react";
import { TestQuestionFormValues } from "src/models/TestQuestion.model";
import { useEditTestQuestionMutation, useGetTestQuestionByIdQuery } from "src/services/TestQuestionServices";
import { showToast } from "src/utils/showToaster";
import { array, boolean, object, string } from "yup";
import JobForm from "../Layout/TestQuestionForm";
import { useNavigate, useParams } from "react-router-dom";


const EditTestQuestionFormWrapper = () => {

  const { id = "" } = useParams();
  const navigate = useNavigate()
  const [saveNextChecked, setSaveNextChecked] = useState(true);
  const [testQuestionData, setTestQuestion] = useState<any>({});

  const [editTestQuestion] = useEditTestQuestionMutation();
  const { data, isLoading, isFetching } = useGetTestQuestionByIdQuery({
    id: id,
  }, { skip: !id });

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setTestQuestion(data?.data);
    }
  }, [data, isLoading, isFetching]);

  // Form Initial Values
  const initialValues: TestQuestionFormValues = {
    questionType: {
      label: testQuestionData?.questionType || '',
      value: testQuestionData?.questionType || ''
    },
    problemAnswerType: testQuestionData?.problemAnswerType === 'MULTIPLE' ? true : false,
    // questionTag: [],
    // questionTechnologyId: [],
    questionTechnologyId: testQuestionData?.questionTechnologyDetails?.map((el: any) => {
      return {
        label: el?.questionTechnology,
        value: el?._id
      }
    }),
    questionTag: testQuestionData?.questionTag || [],
    questionCategoryId: testQuestionData?.questionCategoryDetails?.map((el: any) => {
      return {
        label: el?.categoryName,
        value: el?._id
      }
    }),
    questionTitle: testQuestionData?.questionTitle || '',
    questionDescription: testQuestionData?.description || '',
    questionOptions: testQuestionData?.questionOption?.map((el: any) => el || ''),
    answer: testQuestionData?.answer || '',

  };

  // Validation Schema
  const validationSchema = object().shape({
    questionType: object().required("Question type is required"),
    questionCategoryId: array().min(1, "At least one category is required"),
    questionTag: array().min(1, "At least one tag is required"),
    questionTechnologyId: array().min(1, "At least one technology is required"),
    questionTitle: string().required("Question title is required"),
    questionDescription: string().required("Question description is required"),
    questionOptions: array().of(
      object().shape({
        optionName: string().test(
          'isRequiredForMCQ',
          'Option name is required',
          function (value) {
            const { questionType }: any = this.options.context;
            if (questionType?.value === 'MCQ') {
              return !!value;
            }
            return true;
          }
        ),
        isCorrect: boolean().required("isCorrect is required").test(
          "atLeastOneCorrect",
          "At least one choose correct answer",
          function (options: any) {
            const { questionType, questionOptions }: any = this.options.context;
            if (questionType?.value === "MCQ") {
              let isCheck = questionOptions?.some((option: any) => option.isCorrect)
              return isCheck;
            }
            return true;
          }
        ),
      })
    )
  });

  // Handle Submit
  const handleSubmit = (
    values: TestQuestionFormValues,
    { setSubmitting, resetForm }: FormikHelpers<TestQuestionFormValues>
  ) => {

    const formatedValue = {
      questionType: values?.questionType?.value,
      problemAnswerType:
        values?.questionType?.value === "MCQ"
          ? values?.problemAnswerType
            ? "MULTIPLE"
            : "SINGLE"
          : "",
      questionCategoryId: values?.questionCategoryId?.map(
        (category: any) => category?.value
      ),
      questionTag: values?.questionTag,
      questionTechnologyId: values?.questionTechnologyId?.map(
        (technology: any) => technology?.value
      ),
      questionTitle: values?.questionTitle,
      description: values?.questionDescription,
      questionOption: values?.questionOptions.map((option: any) => {
        const newOption = { ...option };
        delete newOption?._id;
        return newOption;
      }),
      answer: values?.answer,
    };
    editTestQuestion({ body: formatedValue, id: id }).then((res: any) => {
      if (res?.error) {
        showToast("error", res?.error?.data?.message);
      } else {
        if (res?.data?.status) {
          showToast("success", res?.data?.message);
          navigate('/test-module/questions')
          if (!saveNextChecked) {
            resetForm();
          }
        } else {
          showToast("error", res?.data?.message);
        }
      }
      setSubmitting(false);
    });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formikProps) => (
        <Form className="h-full overflow-hidden contents">
          {(isLoading || isFetching) && (
            <div className="absolute w-[100%] h-[100%] flex justify-center items-center z-10 bg-slate-100 opacity-50">
              <CircularProgress />
            </div>
          )}
          <JobForm
            formikProps={formikProps}
            formType="Edit"
            setSaveNextChecked={setSaveNextChecked}
            saveNextChecked={saveNextChecked}
          />
        </Form>
      )}
    </Formik>
  );
};

export default EditTestQuestionFormWrapper;
